/**
 * Trend Rover V2 Dashboard Overlay
 * Neon dark design: dark card, coloured cell backgrounds, shield formula banner.
 * 3-column grid: BUY (dark-green cells) | SIGNAL labels | SELL (dark-red cells)
 * Hit cells turn purple (TP) or amber (SL).
 * CSS prefix: tr-
 */
import React from "react";
import { TrendRoverDashboard } from "../../indicators/built-in/trend-rover";
import "./TrendRoverOverlay.scss";

interface TrendRoverOverlayProps {
  dashboard: TrendRoverDashboard | null;
  symbol: string;
  visible: boolean;
  showDashboard?: boolean;
  onToggleDashboard?: () => void;
  arrowBadges?: { x: number; y: number; direction: "long" | "short"; type?: string; position?: string }[];
}

/** Shield-checkmark icon used in the formula banner */
const ShieldCheck: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="tr-shield-svg">
    <path d="M12 2L4 6v5c0 5.25 3.46 10.17 8 11.5C16.54 21.17 20 16.25 20 11V6L12 2z" fill="rgba(255,255,255,0.85)" />
    <path d="M10 14.4l-2.7-2.7 1.05-1.05L10 12.3l5.65-5.65 1.05 1.05L10 14.4z" fill="#1B5E20" />
  </svg>
);

const TrendRoverOverlay: React.FC<TrendRoverOverlayProps> = ({
  dashboard, symbol, visible,
  showDashboard = true, onToggleDashboard, arrowBadges = [],
}) => {
  if (!visible || !dashboard) return null;

  const {
    formula,
    buyEntry, buyTP1, buyTP2, buySL,
    sellEntry, sellTP1, sellTP2, sellSL,
    buyActive, sellActive,
    buyTP1Hit, buyTP2Hit, buySLHit,
    sellTP1Hit, sellTP2Hit, sellSLHit,
    currentPrice, buyBookedPL, sellBookedPL,
    buyDayPL, sellDayPL,
    buyTradeCount, sellTradeCount,
    buySLCount, sellSLCount,
    buyTP1Count, buyTP2Count,
    sellTP1Count, sellTP2Count,
  } = dashboard;

  const fmt = (v: number | null) => (v !== null ? v.toFixed(2) : "—");
  const fmtPL = (v: number) => v.toFixed(2);

  // ── Which side had/has trades ────────────────────────────────
  const activeSide: "buy" | "sell" | null =
    buyActive   ? "buy"  :
    sellActive  ? "sell" :
    buyTradeCount  > 0 ? "buy"  :
    sellTradeCount > 0 ? "sell" : null;

  const tradeIcon  = activeSide === "sell" ? "▼" : "▲";
  const tradeCount = activeSide === "buy"  ? buyTradeCount
                   : activeSide === "sell" ? sellTradeCount : 0;

  // ── TP / SL badge ────────────────────────────────────────────
  const tp2Count = activeSide === "buy"  ? buyTP2Count  : activeSide === "sell" ? sellTP2Count  : 0;
  const tp1Count = activeSide === "buy"  ? buyTP1Count  : activeSide === "sell" ? sellTP1Count  : 0;
  const slCount  = activeSide === "buy"  ? buySLCount   : activeSide === "sell" ? sellSLCount   : 0;

  let tpBadgeText = "";
  let tpBadgeMod  = "";
  if      (tp2Count > 0) { tpBadgeText = `${tp2Count} TP`;  tpBadgeMod = "tr-tp-badge--tp2"; }
  else if (tp1Count > 0) { tpBadgeText = `${tp1Count} TP1`; tpBadgeMod = "tr-tp-badge--tp1"; }
  else if (slCount  > 0) { tpBadgeText = `${slCount} SL`;   tpBadgeMod = "tr-tp-badge--sl";  }

  // ── Status text ──────────────────────────────────────────────
  const tp2Hit = activeSide === "buy"  ? buyTP2Hit  : activeSide === "sell" ? sellTP2Hit  : false;
  const tp1Hit = activeSide === "buy"  ? buyTP1Hit  : activeSide === "sell" ? sellTP1Hit  : false;
  const slHit  = activeSide === "buy"  ? buySLHit   : activeSide === "sell" ? sellSLHit   : false;
  const isLive = buyActive || sellActive;

  let statusText  = "Waiting...";
  let statusClass = "tr-val-wait";
  if (activeSide) {
    if (isLive) {
      statusText  = tp1Hit ? "TP1-SL"  : "Active";
      statusClass = tp1Hit ? "tr-val-tp1sl" : "tr-val-pos";
    } else {
      if      (tp2Hit)             { statusText = "TP2 ✓";    statusClass = "tr-val-tp2";   }
      else if (tp1Hit && slHit)    { statusText = "TP1→SL";   statusClass = "tr-val-tp1sl"; }
      else if (slHit)              { statusText = "SL ✗";     statusClass = "tr-val-sl";    }
      else                         { statusText = "Waiting..."; statusClass = "tr-val-wait"; }
    }
  }

  // ── Day P&L ─────────────────────────────────────────────────
  const dayPL = activeSide === "buy"
    ? buyDayPL  + (buyActive  ? buyBookedPL  : 0)
    : activeSide === "sell"
    ? sellDayPL + (sellActive ? sellBookedPL : 0)
    : null;
  const dayPLClass = dayPL == null ? "tr-val-dim"
                   : dayPL > 0    ? "tr-val-pos"
                   : dayPL < 0    ? "tr-val-neg" : "";

  // ── Side label ───────────────────────────────────────────────
  const sideLabel     = activeSide === "buy" ? "BUY" : activeSide === "sell" ? "SELL" : "—";
  const sideLabelMod  = activeSide === "buy" ? "tr-pl-side-buy"
                      : activeSide === "sell" ? "tr-pl-side-sell" : "tr-pl-side-wait";

  // ── Grid row data ────────────────────────────────────────────
  const gridRows = [
    {
      label: "ENTRY",   labelType: "entry",
      buyText:  fmt(buyEntry),  sellText:  fmt(sellEntry),
      buyCellMod: "",           sellCellMod: "",
    },
    {
      label: "TGT - 1", labelType: "tgt",
      buyText:  buyTP1Hit  ? `${fmt(buyTP1)} ✓`  : fmt(buyTP1),
      sellText: sellTP1Hit ? `${fmt(sellTP1)} ✓` : fmt(sellTP1),
      buyCellMod:  buyTP1Hit  ? "tr-cell--hit" : "",
      sellCellMod: sellTP1Hit ? "tr-cell--hit" : "",
    },
    {
      label: "TGT - 2", labelType: "tgt",
      buyText:  buyTP2Hit  ? `${fmt(buyTP2)} ✓`  : fmt(buyTP2),
      sellText: sellTP2Hit ? `${fmt(sellTP2)} ✓` : fmt(sellTP2),
      buyCellMod:  buyTP2Hit  ? "tr-cell--hit" : "",
      sellCellMod: sellTP2Hit ? "tr-cell--hit" : "",
    },
    {
      label: "SL",      labelType: "sl",
      buyText:  buySLHit  ? `${fmt(buySL)} ✗`  : fmt(buySL),
      sellText: sellSLHit ? `${fmt(sellSL)} ✗` : fmt(sellSL),
      buyCellMod:  buySLHit  ? "tr-cell--sl-hit" : "",
      sellCellMod: sellSLHit ? "tr-cell--sl-hit" : "",
    },
  ];

  return (
    <>
      {/* Arrow Badges */}
      {arrowBadges.map((badge, i) => {
        const label = badge.type === "tp1" ? "TGT-1 ✓"
                    : badge.type === "tp2" ? "TGT-2 ✓"
                    : badge.type === "sl"  ? "SL ✗"
                    : badge.direction === "long" ? "▲ Buy" : "▼ Sell";
        const cls = badge.type === "sl"
          ? "tr-arrow-badge tr-arrow-badge--sl"
          : (badge.type === "tp1" || badge.type === "tp2")
          ? "tr-arrow-badge tr-arrow-badge--long"
          : badge.direction === "long"
          ? "tr-arrow-badge tr-arrow-badge--long"
          : "tr-arrow-badge tr-arrow-badge--short";
        const top = badge.position === "above" ? badge.y - 44
                  : badge.position === "below" ? badge.y + 30
                  : badge.direction === "long"  ? badge.y + 30 : badge.y - 44;
        return (
          <div key={i} className={cls} style={{ left: badge.x, top }}>
            {label}
          </div>
        );
      })}

      <div className={`tr-overlay${showDashboard ? "" : " tr-overlay--collapsed"}`}>
        {showDashboard && (
          <>
            {/* Header — symbol + price */}
            <div className="tr-header">
              <div className="tr-header-top">
                <span className="tr-symbol">{symbol}</span>
                <button
                  className="tr-toggle-btn"
                  onClick={onToggleDashboard}
                  title="Hide dashboard"
                  aria-label="Hide dashboard"
                >▲</button>
              </div>
              <span className="tr-price">{currentPrice.toFixed(2)}</span>
            </div>

            {/* Formula banner with shields */}
            <div className="tr-formula-banner">
              <ShieldCheck />
              <span className="tr-formula-name">{formula}</span>
              <ShieldCheck />
            </div>

            {/* Column headers */}
            <div className="tr-col-headers">
              <span className="tr-ch-buy">BUY</span>
              <span className="tr-ch-signal">SIGNAL</span>
              <span className="tr-ch-sell">SELL</span>
            </div>

            {/* Data rows */}
            <div className="tr-rows">
              {gridRows.map((row, i) => (
                <div key={i} className="tr-row">
                  <div className={`tr-cell tr-cell--buy${row.buyCellMod ? " " + row.buyCellMod : ""}`}>
                    {row.buyText}
                  </div>
                  <div className={`tr-row-label tr-row-label--${row.labelType}`}>{row.label}</div>
                  <div className={`tr-cell tr-cell--sell${row.sellCellMod ? " " + row.sellCellMod : ""}`}>
                    {row.sellText}
                  </div>
                </div>
              ))}
            </div>

            {/* Trade summary — entry count + TP badge */}
            {tradeCount > 0 && (
              <div className="tr-trade-summary">
                <span className={`tr-entry-info${activeSide === "sell" ? " tr-entry-info--sell" : ""}`}>
                  {tradeIcon} {tradeCount} entr{tradeCount === 1 ? "y" : "ies"}
                </span>
                {tpBadgeText && (
                  <span className={`tr-tp-badge ${tpBadgeMod}`}>{tpBadgeText}</span>
                )}
              </div>
            )}

            {/* Live_Status divider */}
            <div className="tr-status-divider">
              <span className="tr-status-line" />
              <span className="tr-status-text">Live_Status</span>
              <span className="tr-status-line" />
            </div>

            {/* P&L panels */}
            <div className="tr-pl-row">
              <div className={`tr-pl-side-label ${sideLabelMod}`}>{sideLabel}</div>
              <div className="tr-pl-panel">
                <span className="tr-pl-label">Day P&amp;L</span>
                <span className={`tr-pl-val ${dayPLClass}`}>
                  {dayPL !== null ? fmtPL(dayPL) : "—"}
                </span>
              </div>
              <div className="tr-pl-panel">
                <span className="tr-pl-label">Status</span>
                <span className={`tr-pl-val ${statusClass}`}>{statusText}</span>
              </div>
            </div>

            {/* Footer */}
            <div className="tr-footer">TREND ROVER</div>
          </>
        )}

        {!showDashboard && (
          <>
            <div className="tr-collapsed-title">Trend Rover</div>
            <button
              className="tr-toggle-btn"
              onClick={onToggleDashboard}
              title="Show dashboard"
              aria-label="Show dashboard"
            >▼</button>
          </>
        )}
      </div>
    </>
  );
};

export default TrendRoverOverlay;
